import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ComponentType, type ReactNode } from "react";
import { NavLink, useLocation, useNavigationType } from "react-router-dom";
import * as RD from "@radix-ui/react-dialog";
import { Menu as MenuIcon, X } from "lucide-react";
import { Button } from "./Button";
import { VixioSymbol } from "./Brand";
import { TopProgress } from "./TopProgress";
import { cx } from "../lib/cx";

export interface NavItem {
  to: string;
  label: string;
  icon?: ComponentType<{ "aria-hidden"?: boolean | "true" | "false" }>;
  /** Só marca ativo na rota exata. */
  end?: boolean;
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

export function Wordmark({ label = "Vixio OS" }: { label?: string }) {
  return (
    <>
      <VixioSymbol size={18} />
      <span className="vx-wordmark">{label}</span>
    </>
  );
}

export function Sidebar({ groups, footer, brandTo = "/", brandLabel, onNavigate }: {
  groups: NavGroup[];
  footer?: ReactNode;
  brandTo?: string;
  /** Rótulo ao lado da marca (padrão "Vixio OS"; o portal usa "Área do cliente"). */
  brandLabel?: string;
  onNavigate?: () => void;
}) {
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const [ind, setInd] = useState<{ y: number; h: number } | null>(null);
  const [ready, setReady] = useState(false);
  // Realce do item ativo que desliza de um item para o outro: mede o item atual (e de novo se o menu mudar de tamanho).
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      const el = nav.querySelector<HTMLElement>('.vx-nav__item[aria-current="page"]');
      setInd(el && el.offsetHeight > 0 ? { y: el.offsetTop, h: el.offsetHeight } : null);
    };
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(nav);
    return () => ro?.disconnect();
  }, [pathname, groups]);
  // Só anima depois da primeira posição (abrir a página não mostra o realce viajando do topo).
  useEffect(() => {
    if (!ind || ready) return;
    const t = window.setTimeout(() => setReady(true), 50);
    return () => window.clearTimeout(t);
  }, [ind, ready]);
  const indStyle = ind ? ({ "--vx-ind-y": `${ind.y}px`, height: ind.h } as CSSProperties) : undefined;
  return (
    <>
      <NavLink to={brandTo} end className="vx-brand" onClick={onNavigate}>
        <Wordmark label={brandLabel} />
      </NavLink>
      <nav ref={navRef} className={cx("vx-nav", ind && "vx-nav--ind", ready && "vx-nav--ready")} aria-label="Menu principal">
        <span className="vx-nav__indicator" style={indStyle} aria-hidden="true" />
        {groups.map((g) =>
          g.items.length === 0 ? null : (
            <div key={g.key} className="vx-nav__group" role="group" aria-labelledby={`nav-${g.key}`}>
              {g.label ? (
                <span className="vx-nav__group-label" id={`nav-${g.key}`}>
                  {g.label}
                </span>
              ) : null}
              {g.items.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink key={it.to} to={it.to} end={it.end} className="vx-nav__item" onClick={onNavigate}>
                    {Icon ? <Icon aria-hidden="true" /> : null}
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ),
        )}
      </nav>
      {footer ? <div className="vx-sidebar__foot">{footer}</div> : null}
    </>
  );
}

export interface AppShellProps {
  groups: NavGroup[];
  topbar: ReactNode;
  sidebarFooter?: ReactNode;
  children: ReactNode;
  brandTo?: string;
  brandLabel?: string;
}

export function AppShell({ groups, topbar, sidebarFooter, children, brandTo, brandLabel }: AppShellProps) {
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();
  const navType = useNavigationType();
  useEffect(() => setDrawer(false), [location.pathname]);
  // Tela nova começa do topo (voltar/avançar do navegador mantém a posição que o navegador restaura).
  useEffect(() => {
    if (navType !== "POP") document.documentElement.scrollTop = 0;
  }, [location.pathname, navType]);
  // Barra superior ganha sombra quando a página rola.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let frame = 0;
    const read = () => setScrolled(window.scrollY > 4);
    const onScroll = () => {
      window.cancelAnimationFrame?.(frame);
      frame = window.requestAnimationFrame ? window.requestAnimationFrame(read) : (read(), 0);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.cancelAnimationFrame?.(frame);
    };
  }, []);
  // Mantém o item ativo visível na barra lateral (menus longos rolam).
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('.vx-sidebar .vx-nav__item[aria-current="page"]');
    el?.scrollIntoView?.({ block: "nearest" });
  }, [location.pathname]);

  return (
    <div className="vx-shell">
      <a className="skip-link" href="#conteudo">
        Pular para o conteúdo
      </a>
      <aside className="vx-sidebar">
        <div className="vx-sidebar__inner">
          <Sidebar groups={groups} footer={sidebarFooter} brandTo={brandTo} brandLabel={brandLabel} />
        </div>
      </aside>
      <header className="vx-topbar" data-scrolled={scrolled ? "" : undefined}>
        <RD.Root open={drawer} onOpenChange={setDrawer}>
          <RD.Trigger asChild>
            <Button className="vx-topbar__menu" variant="ghost" iconOnly label="Abrir menu" icon={<MenuIcon />} />
          </RD.Trigger>
          <RD.Portal>
            <RD.Overlay className="vx-overlay" />
            <RD.Content className="vx-drawer-nav" aria-describedby={undefined}>
              <RD.Title className="sr-only">Menu</RD.Title>
              <div style={{ position: "absolute", right: 8, top: 8 }}>
                <RD.Close asChild>
                  <Button variant="ghost" size="sm" iconOnly label="Fechar menu" icon={<X />} />
                </RD.Close>
              </div>
              <Sidebar groups={groups} footer={sidebarFooter} brandTo={brandTo} brandLabel={brandLabel} onNavigate={() => setDrawer(false)} />
            </RD.Content>
          </RD.Portal>
        </RD.Root>
        {topbar}
        <TopProgress />
      </header>
      <main className="vx-main" id="conteudo" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}
